import React, {
  useEffect,
  useRef,
  useState,
  useCallback,
  useMemo,
  useContext,
} from "react";
import {
  Box,
  Grid,
  Paper,
  Typography,
  TextField,
  Button,
  Collapse,
  IconButton,
  ToggleButton,
  ToggleButtonGroup,
  Autocomplete,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  RadioGroup,
  FormControlLabel,
  Radio,
  Link,
  CircularProgress,
  Chip,
  Tooltip,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Divider,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import DeleteIcon from "@mui/icons-material/Delete";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import LaunchIcon from "@mui/icons-material/Launch";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import axios from "axios";
import WhatsAppBookingDialog from "./WhatsAppBookingDialog";
import { AutoTaskAuthContext } from "../../Context/AutoTaskContext";
import { useBranchCreateGuard } from "../BranchCreateGuard";
// Shared keyboard-first date/time field, aliased to the local name that
// used to be defined further down so no call site had to change.
import { StringDateTimeField as SingleDatePicker } from "../DateTimeField";

const API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL || "http://localhost:3000";

const STAGE_OPTIONS = [
  "Hot Leads",
  "Warm Leads",
  "Cold Leads",
  "Contacted",
  "Demo Scheduled",
  "Demo Completed",
  "Proposal / Quotation Sent",
  "Negotiation",
  "Decision Pending",
  "Customer",
  "Lost",
  "On Hold",
  "Future",
];

// =====================================================================
// Shared calendar helpers (unchanged)
// =====================================================================
const MONTH_NAMES = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];
const DAY_HEADERS = ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"];

function buildCalendarDays(year, month) {
  const firstDay = new Date(year, month, 1).getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const prevMonthDays = new Date(year, month, 0).getDate();
  const cells = [];
  for (let i = firstDay - 1; i >= 0; i--) {
    cells.push({
      date: new Date(year, month - 1, prevMonthDays - i),
      outside: true,
    });
  }
  for (let d = 1; d <= daysInMonth; d++) {
    cells.push({ date: new Date(year, month, d), outside: false });
  }
  const remaining = 42 - cells.length;
  for (let d = 1; d <= remaining; d++) {
    cells.push({ date: new Date(year, month + 1, d), outside: true });
  }
  return cells;
}

function toYMD(date) {
  if (!date) return "";
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

function fromYMD(str) {
  if (!str) return null;
  const [y, m, d] = str.split("-").map(Number);
  if (!y || !m || !d) return null;
  return new Date(y, m - 1, d);
}

function isSameDay(a, b) {
  if (!a || !b) return false;
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

function to12h(time24) {
  if (!time24) return { h12: "12", mm: "00", period: "AM" };
  const [rawH, rawM = "00"] = time24.split(":");
  let h = parseInt(rawH, 10);
  if (isNaN(h)) return { h12: "12", mm: "00", period: "AM" };
  const period = h >= 12 ? "PM" : "AM";
  h = h % 12;
  if (h === 0) h = 12;
  return { h12: String(h), mm: rawM.padStart(2, "0"), period };
}

function to24h(h12, mm, period) {
  let h = parseInt(h12, 10);
  if (isNaN(h)) h = 12;
  if (period === "AM") {
    if (h === 12) h = 0;
  } else {
    if (h !== 12) h += 12;
  }
  return `${String(h).padStart(2, "0")}:${String(mm).padStart(2, "0")}`;
}

// =====================================================================
// TimeSelector component (unchanged)
// =====================================================================
const TimeSelector = ({ time24, onChange, label }) => {
  const isUnset = !time24;
  const { h12, mm, period } = to12h(time24 || "");
  const hours = Array.from({ length: 12 }, (_, i) => String(i + 1));
  const minutes = Array.from({ length: 12 }, (_, i) =>
    String(i * 5).padStart(2, "0"),
  );
  const safeH = isUnset ? "12" : h12;
  const safeM = isUnset ? "00" : mm;
  const safeP = isUnset ? "AM" : period;
  const handleH = (e) => onChange(to24h(e.target.value, safeM, safeP));
  const handleM = (e) => onChange(to24h(safeH, e.target.value, safeP));
  const handleP = (e) => onChange(to24h(safeH, safeM, e.target.value));
  const selectSx = {
    border: "1px solid #ccc",
    borderRadius: "4px",
    fontSize: "0.8rem",
    padding: "2px 4px",
    cursor: "pointer",
    outline: "none",
    background: "#f9f9f9",
    fontWeight: 600,
  };
  const placeholderSx = { ...selectSx, color: "#aaa" };
  return (
    <Box sx={{ textAlign: "center", mb: 1 }}>
      <Typography
        variant="caption"
        sx={{ display: "block", mb: 0.5, fontWeight: 600, color: "#555" }}
      >
        {label}
      </Typography>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: "4px",
        }}
      >
        <Box
          component="select"
          value={isUnset ? "" : h12}
          onChange={handleH}
          sx={isUnset ? placeholderSx : selectSx}
        >
          {isUnset && (
            <option value="" disabled>
              HH
            </option>
          )}
          {hours.map((h) => (
            <option key={h} value={h}>
              {h}
            </option>
          ))}
        </Box>
        <Typography
          sx={{
            fontWeight: 700,
            fontSize: "0.9rem",
            color: isUnset ? "#aaa" : "inherit",
          }}
        >
          :
        </Typography>
        <Box
          component="select"
          value={isUnset ? "" : mm}
          onChange={handleM}
          sx={isUnset ? placeholderSx : selectSx}
        >
          {isUnset && (
            <option value="" disabled>
              MM
            </option>
          )}
          {minutes.map((m) => (
            <option key={m} value={m}>
              {m}
            </option>
          ))}
        </Box>
        <Box
          component="select"
          value={safeP}
          onChange={handleP}
          sx={isUnset ? placeholderSx : selectSx}
        >
          <option value="AM">AM</option>
          <option value="PM">PM</option>
        </Box>
      </Box>
    </Box>
  );
};


// =====================================================================
// Helper Functions (unchanged)
// =====================================================================
const parseDDMMYYYY = (dateStr) => {
  if (!dateStr) return null;
  if (dateStr instanceof Date) return dateStr;
  if (typeof dateStr === "string") {
    const parts = dateStr.split("-");
    if (parts.length !== 3) return null;
    const [dd, mm, yyyy] = parts.map(Number);
    return new Date(yyyy, mm - 1, dd + 1);
  }
  return null;
};

const getDateString = (daysFromToday = 0) => {
  const date = new Date();
  date.setDate(date.getDate() + daysFromToday);
  return date.toISOString().split("T")[0];
};

const getValidPickupDate = (selectedDateStr) => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  if (!selectedDateStr) return getDateString(1);
  const sel = parseDDMMYYYY(selectedDateStr);
  if (!sel || isNaN(sel.getTime())) return getDateString(1);
  sel.setHours(0, 0, 0, 0);
  if (sel < today) return today.toISOString().split("T")[0];
  return sel.toISOString().split("T")[0];
};

const extractPdf = (raw) => {
  if (!raw) return null;
  if (typeof raw !== "object") return null;
  const file = raw.file || raw.url || raw.fileUrl || "";
  if (!file || typeof file !== "string" || !file.trim()) return null;
  return {
    file: file.trim(),
    fileName: raw.fileName || raw.filename || raw.name || "Itinerary.pdf",
  };
};

// =====================================================================
// Availability Helper Functions (unchanged)
// =====================================================================
const isDriverBlocked = (driver, checkDate) => {
  if (!driver || driver.status !== "B") return false;
  if (!driver.blockedFrom || !driver.blockedTo) return false;

  const blockedFrom = new Date(driver.blockedFrom);
  const blockedTo = new Date(driver.blockedTo);
  const check = new Date(checkDate);

  blockedFrom.setHours(0, 0, 0, 0);
  blockedTo.setHours(23, 59, 59, 999);
  check.setHours(0, 0, 0, 0);

  return check >= blockedFrom && check <= blockedTo;
};

const timeRangesOverlap = (start1, end1, start2, end2) => {
  if (!start1 || !end1 || !start2 || !end2) return false;
  const s1 = new Date(start1).getTime();
  const e1 = new Date(end1).getTime();
  const s2 = new Date(start2).getTime();
  const e2 = new Date(end2).getTime();
  return s1 < e2 && e1 > s2;
};

const getAvailableVehicles = (
  allVehicles,
  bookings,
  currentPickupDate,
  currentPickupTime,
  currentDropDate,
  currentDropTime,
  currentBookingId,
) => {
  if (!currentPickupDate) return allVehicles;
  const hasCompleteTimeRange =
    currentPickupDate &&
    currentPickupTime &&
    currentDropDate &&
    currentDropTime;
  if (!hasCompleteTimeRange) return allVehicles;
  const currentStart = new Date(
    `${currentPickupDate}T${currentPickupTime || "00:00"}`,
  );
  const currentEnd = new Date(
    `${currentDropDate}T${currentDropTime || "23:59"}`,
  );
  return allVehicles.filter((vehicle) => {
    const vehicleBookings = bookings.filter((booking) => {
      if (currentBookingId && booking.booking_id === currentBookingId)
        return false;
      return booking.vehicle_id === vehicle.vehicle_id;
    });
    const hasConflict = vehicleBookings.some((booking) => {
      if (!booking.pickup_date || !booking.drop_date) return false;
      const bookingStart = new Date(
        `${booking.pickup_date}T${booking.pickup_time || "00:00"}`,
      );
      const bookingEnd = new Date(
        `${booking.drop_date}T${booking.drop_time || "23:59"}`,
      );
      return timeRangesOverlap(
        currentStart,
        currentEnd,
        bookingStart,
        bookingEnd,
      );
    });
    return !hasConflict;
  });
};

const getAvailableDrivers = (
  allDrivers,
  bookings,
  currentPickupDate,
  currentPickupTime,
  currentDropDate,
  currentDropTime,
  currentBookingId,
) => {
  const eligibleDrivers = allDrivers.filter((driver) => {
    if (driver.status === "I") return false;
    if (driver.status === "B") {
      const blockedFrom = driver.blockedFrom || driver.blocked_from;
      const blockedTo = driver.blockedTo || driver.blocked_to;
      if (!blockedFrom || !blockedTo) return false;
      if (currentPickupDate) {
        const tripStart = new Date(currentPickupDate);
        const tripEnd = currentDropDate
          ? new Date(currentDropDate)
          : new Date(currentPickupDate);
        const bFrom = new Date(blockedFrom);
        const bTo = new Date(blockedTo);
        tripStart.setHours(0, 0, 0, 0);
        tripEnd.setHours(0, 0, 0, 0);
        bFrom.setHours(0, 0, 0, 0);
        bTo.setHours(23, 59, 59, 999);
        if (tripStart <= bTo && tripEnd >= bFrom) return false;
      }
    } else if (driver.status !== "A") {
      return false;
    }
    return true;
  });
  const hasCompleteTimeRange =
    currentPickupDate &&
    currentPickupTime &&
    currentDropDate &&
    currentDropTime;
  if (!hasCompleteTimeRange) return eligibleDrivers;
  const currentStart = new Date(`${currentPickupDate}T${currentPickupTime}`);
  const currentEnd = new Date(`${currentDropDate}T${currentDropTime}`);
  return eligibleDrivers.filter((driver) => {
    const driverBookings = bookings.filter((booking) => {
      if (currentBookingId && booking.booking_id === currentBookingId)
        return false;
      return booking.driver_id === driver.id;
    });
    const hasConflict = driverBookings.some((booking) => {
      if (!booking.pickup_date || !booking.drop_date) return false;
      const bookingStart = new Date(
        `${booking.pickup_date}T${booking.pickup_time || "00:00"}`,
      );
      const bookingEnd = new Date(
        `${booking.drop_date}T${booking.drop_time || "23:59"}`,
      );
      return timeRangesOverlap(
        currentStart,
        currentEnd,
        bookingStart,
        bookingEnd,
      );
    });
    return !hasConflict;
  });
};

// =====================================================================
// Main BookingForm Component (modified to use two separate pickers)
// =====================================================================
export default function BookingForm({
  store_id,
  user_id,
  editBookingId,
  customerDetail,
  onResetFormHandler,
  selectedBooking,
  setSelectedBooking,
  bookings,
  fetchBookings,
  allVehicles,
  allDrivers,
  setSnackbar,
  navigate,
  location,
  userDataObject,
  setSelectedCustomerForPanel,
  setSelectedCustomerTrips,
  onItineraryUseHandler,
  onCustomerSelectHandler,
  onAfterSave,
}) {
  const { userInfo, branchScope } = useContext(AutoTaskAuthContext) || {};

  // A new booking records the branch that took it — see handleSaveBooking.
  const {
    blocked: branchBlocked,
    openBranchPicker,
    guardDialog: branchGuardDialog,
  } = useBranchCreateGuard({
    reason:
      "You are viewing every branch. Pick the branch taking this booking " +
      "before saving it.",
  });

  const mobileTabPressedRef = useRef(false);
  const isLoadingBookingRef = useRef(false);
  const mobileInputRef = useRef(null);
  const manualClearRef = useRef({ vehicle: false, driver: false });

  const selectedDateFromState = location?.state?.selectedDate;

  const [form, setForm] = useState({
    partyCode: "",
    bookingType: "B",
    customerName: "",
    customerMobile: "",
    tripDetails: "",
    pickupDate: getValidPickupDate(selectedDateFromState),
    pickupTime: "",
    pax: "",
    pickupLocation: "",
    pickupLocationLabel: "",
    pickupAddress: "",
    dropLocation: "",
    dropAddress: "",
    dropDate: "", // will be set to pickupDate via useEffect
    dropTime: "",
    tripMode: "DROP",
    quote: "",
    advance: "",
    vehicleId: null,
    driverId: null,
    confirmation: "WHATSAPP",
    paymentDetails: "",
    instructions: "",
    vehicleType: "",
    stage: "Customer",
    itinerary_docs: null,
  });

  const [errors, setErrors] = useState({});
  const [isEdited, setIsEdited] = useState(false);
  const [showMoreFields, setShowMoreFields] = useState(true);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [bookingToDelete, setBookingToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [whatsappDialogOpen, setWhatsappDialogOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const [customerExists, setCustomerExists] = useState(false);
  const [customerData, setCustomerData] = useState(null);
  const [showCustomerDialog, setShowCustomerDialog] = useState(false);

  const [showPickupLocationDialog, setShowPickupLocationDialog] =
    useState(false);
  const [availablePickupLocations, setAvailablePickupLocations] = useState([]);

  const [selectedVehicle, setSelectedVehicle] = useState(null);
  const [selectedDriver, setSelectedDriver] = useState(null);

  const [otherFlags, setOtherFlags] = useState({
    isVehicleOther: false,
    isDriverOther: false,
  });
  const [tempVehicle, setTempVehicle] = useState("");
  const [tempDriver, setTempDriver] = useState("");

  const attachedPdf = extractPdf(form.itinerary_docs);

  // ── COMPUTE AVAILABLE VEHICLES AND DRIVERS (unchanged) ──
  const currentBookingId = selectedBooking?.booking_id || editBookingId;

  const availableVehicles = useMemo(() => {
    const available = getAvailableVehicles(
      allVehicles,
      bookings,
      form.pickupDate,
      form.pickupTime,
      form.dropDate,
      form.dropTime,
      currentBookingId,
    );
    return [
      {
        vehicle_id: "other",
        vehicle_name: "Other",
        vehicle_number: "",
        vehicle_code: "Other",
      },
      ...available,
    ];
  }, [
    allVehicles,
    bookings,
    form.pickupDate,
    form.pickupTime,
    form.dropDate,
    form.dropTime,
    currentBookingId,
  ]);

  const availableDrivers = useMemo(() => {
    const available = getAvailableDrivers(
      allDrivers,
      bookings,
      form.pickupDate,
      form.pickupTime,
      form.dropDate,
      form.dropTime,
      currentBookingId,
    );
    return [{ id: "other", driver_code: "Other", name: "" }, ...available];
  }, [
    allDrivers,
    bookings,
    form.pickupDate,
    form.pickupTime,
    form.dropDate,
    form.dropTime,
    currentBookingId,
  ]);

  // ── NEW: Sync dropDate to pickupDate whenever pickupDate changes ──
  useEffect(() => {
    if (form.pickupDate) {
      setForm((prev) => ({
        ...prev,
        dropDate: form.pickupDate,
      }));
    }
  }, [form.pickupDate]);

  // ── Also set initial dropDate when pickupDate is set from elsewhere ──
  // Keyed on pickupDate only: re-running when dropDate empties would refill it
  // while the user is clearing a segment, and fight the date field forever.
  useEffect(() => {
    if (form.pickupDate && !form.dropDate) {
      setForm((prev) => ({
        ...prev,
        dropDate: form.pickupDate,
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.pickupDate]);

  // ── ITINERARY HANDLER (unchanged) ──
  const handleItineraryUse = useCallback(
    (itineraryData) => {
      const tripDetailsContent = `${itineraryData.name}`;
      setForm((prev) => ({
        ...prev,
        tripDetails: tripDetailsContent,
        itinerary_docs: {
          file: itineraryData.pdfBase64,
          fileName: itineraryData.pdfFileName,
        },
      }));
      setSnackbar({
        open: true,
        message: "Itinerary details loaded successfully!",
        severity: "success",
      });
    },
    [setSnackbar],
  );

  const handleRemoveItineraryPdf = () => {
    setForm((prev) => ({ ...prev, itinerary_docs: null }));
    setSnackbar({
      open: true,
      message: "Itinerary PDF removed",
      severity: "info",
    });
  };

  const handleOpenPdfInNewTab = () => {
    const pdf = extractPdf(form.itinerary_docs);
    if (!pdf) return;
    const raw = pdf.file;
    if (raw.startsWith("http://") || raw.startsWith("https://")) {
      window.open(raw, "_blank");
      return;
    }
    try {
      const base64 = raw.includes(",") ? raw.split(",")[1] : raw;
      const byteChars = atob(base64);
      const byteNumbers = new Uint8Array(byteChars.length);
      for (let i = 0; i < byteChars.length; i++) {
        byteNumbers[i] = byteChars.charCodeAt(i);
      }
      const blob = new Blob([byteNumbers], { type: "application/pdf" });
      const blobUrl = URL.createObjectURL(blob);
      window.open(blobUrl, "_blank");
      setTimeout(() => URL.revokeObjectURL(blobUrl), 30000);
    } catch (err) {
      console.error("Failed to open PDF:", err);
      setSnackbar({
        open: true,
        message: "Failed to open PDF. Please try again.",
        severity: "error",
      });
    }
  };

  const handleCustomerAutofill = useCallback(
    (fetched) => {
      setForm((prev) => ({
        ...prev,
        customerName: fetched.name || "",
        customerMobile: fetched.mobile?.toString() || "",
        partyCode: fetched.party_code || "",
        pickupAddress: fetched.pickupAddress || fetched.pickup_address || "",
        stage: fetched.stage || prev.stage,
      }));
      setCustomerExists(true);
      setCustomerData(fetched);
      setSelectedCustomerForPanel(fetched);
      setSelectedCustomerTrips(fetched.trips || []);
      setIsEdited(true);
    },
    [setSelectedCustomerForPanel, setSelectedCustomerTrips],
  );

  const handleReset = useCallback(() => {
    setForm({
      partyCode: "",
      bookingType: "B",
      customerName: "",
      customerMobile: "",
      tripDetails: "",
      pickupDate: getDateString(1),
      pickupTime: "",
      pax: "",
      pickupLocation: "",
      pickupLocationLabel: "",
      pickupAddress: "",
      dropLocation: "",
      dropAddress: "",
      dropDate: getDateString(1), // set to same as pickup
      dropTime: "",
      tripMode: "DROP",
      quote: "",
      advance: "",
      vehicleId: null,
      driverId: null,
      confirmation: "WHATSAPP",
      paymentDetails: "",
      instructions: "",
      vehicleType: "",
      stage: "Customer",
      itinerary_docs: null,
    });
    setSelectedBooking(null);
    setSelectedVehicle(null);
    setSelectedDriver(null);
    setTempVehicle("");
    setTempDriver("");
    setCustomerExists(false);
    setCustomerData(null);
    setErrors({});
    setIsEdited(false);
    setOtherFlags({ isVehicleOther: false, isDriverOther: false });
    setShowMoreFields(false);
    setAvailablePickupLocations([]);
    setSelectedCustomerForPanel(null);
    setSelectedCustomerTrips([]);
    manualClearRef.current = { vehicle: false, driver: false };
  }, [
    setSelectedBooking,
    setSelectedCustomerForPanel,
    setSelectedCustomerTrips,
  ]);

  useEffect(() => {
    if (onResetFormHandler) {
      onResetFormHandler(handleReset);
    }
  }, [onResetFormHandler, handleReset]);

  useEffect(() => {
    if (onItineraryUseHandler) {
      onItineraryUseHandler(handleItineraryUse);
    }
  }, [onItineraryUseHandler, handleItineraryUse]);

  useEffect(() => {
    if (onCustomerSelectHandler) {
      onCustomerSelectHandler(handleCustomerAutofill);
    }
  }, [onCustomerSelectHandler, handleCustomerAutofill]);

  // ── VALIDATION & OTHER EFFECTS (unchanged) ──
  useEffect(() => {
    if (!form.pickupDate || !form.dropDate) {
      if (!form.dropDate && errors.dropDate)
        setErrors((prev) => {
          const n = { ...prev };
          delete n.dropDate;
          return n;
        });
      return;
    }
    const pickupDateTime = new Date(
      `${form.pickupDate}T${form.pickupTime || ""}`,
    );
    const dropDateTime = new Date(`${form.dropDate}T${form.dropTime || ""}`);
    if (isNaN(pickupDateTime.getTime()) || isNaN(dropDateTime.getTime()))
      return;
    if (dropDateTime < pickupDateTime) {
      setSnackbar({
        open: true,
        message: "Drop date/time must be after pickup date/time",
        severity: "error",
      });
      setErrors((prev) => ({
        ...prev,
        dropDate: "Drop date/time must be after pickup date/time",
      }));
    } else {
      setErrors((prev) => {
        const n = { ...prev };
        delete n.dropDate;
        return n;
      });
    }
  }, [form.pickupDate, form.pickupTime, form.dropDate, form.dropTime]);

  useEffect(() => {
    const selectedDate = location?.state?.selectedDate;
    if (!selectedDate) return;
    setForm((prev) => ({
      ...prev,
      pickupDate: getValidPickupDate(selectedDate),
    }));
  }, [location?.state?.selectedDate]);

  useEffect(() => {
    if (form.bookingType === "E") setShowMoreFields(false);
    else setShowMoreFields(false);
  }, [form.bookingType]);

  useEffect(() => {
    const prefilledVehicle = location?.state?.prefilledVehicle;
    if (prefilledVehicle && !editBookingId) {
      setSelectedVehicle({
        vehicle_id: prefilledVehicle.vehicle_id,
        vehicle_name: prefilledVehicle.vehicle_name,
        vehicle_number: prefilledVehicle.vehicle_number,
        vehicle_code: prefilledVehicle.vehicle_code,
      });
      setForm((prev) => ({ ...prev, vehicleId: prefilledVehicle.vehicle_id }));
    }
  }, [location?.state?.prefilledVehicle, editBookingId]);

  useEffect(() => {
    if (!editBookingId || !allVehicles.length || !allDrivers.length) return;

    const loadBookingById = async () => {
      try {
        const response = await axios.get(
          `${API_BASE_URL}/api/travel/booking/${store_id}/${editBookingId}`,
        );
        if (!response?.data?.success) {
          setSnackbar({
            open: true,
            message: "Failed to load booking for editing",
            severity: "error",
          });
          return;
        }
        const booking = response.data.data;
        isLoadingBookingRef.current = true;

        const matchInList = bookings.find(
          (b) => b.booking_id === editBookingId,
        );
        if (matchInList) setSelectedBooking(matchInList);
        else setSelectedBooking({ booking_id: editBookingId, ...booking });

        if (booking.vehicle_id === null && booking.temp_vehicle) {
          setTempVehicle(booking.temp_vehicle || "");
          setSelectedVehicle(null);
          setOtherFlags((prev) => ({ ...prev, isVehicleOther: true }));
        } else {
          setSelectedVehicle(
            allVehicles.find((v) => v.vehicle_id === booking.vehicle_id) ||
              null,
          );
          setTempVehicle("");
          setOtherFlags((prev) => ({ ...prev, isVehicleOther: false }));
        }

        if (
          booking.driver_id === null &&
          (booking.temp_driver || booking.temp_driver_number)
        ) {
          setTempDriver(
            [booking.temp_driver, booking.temp_driver_number]
              .filter(Boolean)
              .join(" "),
          );
          setSelectedDriver(null);
          setOtherFlags((prev) => ({ ...prev, isDriverOther: true }));
        } else {
          setSelectedDriver(
            allDrivers.find((d) => d.id === booking.driver_id) || null,
          );
          setTempDriver("");
          setOtherFlags((prev) => ({ ...prev, isDriverOther: false }));
        }

        let stageValue = "Customer";
        if (booking.customer_full_data?.stage)
          stageValue = booking.customer_full_data.stage;
        else if (booking.customer_stage) stageValue = booking.customer_stage;
        else
          stageValue = booking.booking_type === "B" ? "Customer" : "Cold Leads";

        setForm({
          partyCode: booking.party_code || "",
          bookingType: booking.booking_type || "B",
          customerName: booking.customer_name || "",
          customerMobile: booking.customer_mobile || "",
          tripDetails: booking.trip_details || "",
          pickupDate: booking.pickup_date || "",
          pickupTime: booking.pickup_time || "",
          pax: booking.pax?.toString() || "",
          pickupLocation:
            booking.pickup_location ||
            booking.pickupLocation ||
            booking.trip_info?.pickup_location ||
            "",
          pickupLocationLabel: booking.pickupLocationLabel || "",
          pickupAddress:
            booking.pickup_address ||
            booking.pickupAddress ||
            booking.trip_info?.pickup_address ||
            "",
          dropLocation: booking.drop_location || "",
          dropAddress: booking.drop_address || "",
          dropDate: booking.drop_date || "",
          dropTime: booking.drop_time || "",
          tripMode: booking.trip_mode || "DROP",
          quote: booking.quote || "",
          advance: booking.advance || "",
          vehicleId: booking.vehicle_id || null,
          driverId: booking.driver_id || null,
          confirmation: booking.confirmation || "WHATSAPP",
          paymentDetails: booking.payment_details || "",
          instructions: booking.instructions || "",
          vehicleType: booking.vehicle_type || "",
          stage: stageValue,
          itinerary_docs: extractPdf(
            booking.itinerary_doc || booking.itinerary_docs,
          ),
        });

        setCustomerExists(true);
        setIsEdited(false);
        manualClearRef.current = { vehicle: false, driver: false };

        if (booking.customer_id) {
          try {
            const res = await axios.get(
              `${API_BASE_URL}/api/travel/customers/store/${store_id}/id/${booking.customer_id}`,
            );
            if (res.data.success) {
              const fetched = res.data.data;
              setSelectedCustomerForPanel(fetched);
              setSelectedCustomerTrips(fetched.trips || []);
            }
          } catch (err) {
            console.error("Failed to fetch customer trips:", err);
          }
        }

        setTimeout(() => {
          isLoadingBookingRef.current = false;
        }, 100);
      } catch (error) {
        console.error("Load booking by ID error:", error);
        setSnackbar({
          open: true,
          message:
            error?.response?.data?.message || "Error loading booking for edit",
          severity: "error",
        });
      }
    };

    loadBookingById();
  }, [editBookingId, allVehicles, allDrivers, store_id]);

  // ── AUTO-ASSIGN (unchanged) ──
  useEffect(() => {
    if (editBookingId && !isEdited) return;
    if (tempVehicle || tempDriver) return;
    if (!allDrivers.length || !allVehicles.length) return;

    if (
      selectedVehicle &&
      selectedVehicle.vehicle_id !== "other" &&
      !selectedDriver &&
      !form.driverId &&
      !manualClearRef.current.driver
    ) {
      const linkedDriver = availableDrivers.find(
        (driver) =>
          driver.vehicle_id === selectedVehicle.vehicle_id &&
          driver.id !== "other",
      );
      if (linkedDriver) {
        setSelectedDriver(linkedDriver);
        setForm((prev) => ({ ...prev, driverId: linkedDriver.id }));
        setSnackbar({
          open: true,
          message: `Driver ${linkedDriver.driver_code} auto-assigned`,
          severity: "info",
        });
      }
    }

    if (
      selectedDriver &&
      selectedDriver.id !== "other" &&
      !selectedVehicle &&
      !form.vehicleId &&
      selectedDriver.vehicle_id &&
      !manualClearRef.current.vehicle
    ) {
      const linkedVehicle = availableVehicles.find(
        (vehicle) =>
          vehicle.vehicle_id === selectedDriver.vehicle_id &&
          vehicle.vehicle_id !== "other",
      );
      if (linkedVehicle) {
        setSelectedVehicle(linkedVehicle);
        setForm((prev) => ({ ...prev, vehicleId: linkedVehicle.vehicle_id }));
        setSnackbar({
          open: true,
          message: `Vehicle ${linkedVehicle.vehicle_number} auto-assigned`,
          severity: "info",
        });
      }
    }
  }, [
    selectedVehicle?.vehicle_id,
    selectedDriver?.id,
    availableDrivers,
    availableVehicles,
    tempVehicle,
    tempDriver,
    editBookingId,
    isEdited,
  ]);

  // ── Clear vehicle/driver if they become unavailable (unchanged) ──
  useEffect(() => {
    if (editBookingId && !isEdited) return;

    if (selectedVehicle && selectedVehicle.vehicle_id !== "other") {
      const isAvailable = availableVehicles.some(
        (v) => v.vehicle_id === selectedVehicle.vehicle_id,
      );
      if (!isAvailable && !manualClearRef.current.vehicle) {
        setSelectedVehicle(null);
        setForm((prev) => ({ ...prev, vehicleId: null }));
        setSnackbar({
          open: true,
          message: "Vehicle is no longer available for selected time slot",
          severity: "warning",
        });
      }
    }

    if (selectedDriver && selectedDriver.id !== "other") {
      const isAvailable = availableDrivers.some(
        (d) => d.id === selectedDriver.id,
      );
      if (!isAvailable && !manualClearRef.current.driver) {
        setSelectedDriver(null);
        setForm((prev) => ({ ...prev, driverId: null }));
        setSnackbar({
          open: true,
          message: "Driver is no longer available for selected time slot",
          severity: "warning",
        });
      }
    }
  }, [
    availableVehicles,
    availableDrivers,
    selectedVehicle,
    selectedDriver,
    editBookingId,
    isEdited,
  ]);

  useEffect(() => {
    if (!selectedBooking || !editBookingId || customerExists) return;
    if (form.bookingType === "B")
      setForm((prev) => ({ ...prev, stage: "Customer" }));
    else if (form.bookingType === "E")
      setForm((prev) => ({ ...prev, stage: "Cold Leads" }));
  }, [form.bookingType, selectedBooking, editBookingId, customerExists]);

  useEffect(() => {
    if (customerDetail && !editBookingId) {
      setForm((prev) => ({
        ...prev,
        customerName: customerDetail.name || "",
        customerMobile: customerDetail.mobile?.toString() || "",
        pickupAddress: customerDetail.pickupAddress || "",
      }));
      setCustomerExists(true);
      setCustomerData(customerDetail);
      setSnackbar({
        open: true,
        message: "Customer details loaded successfully!",
        severity: "success",
      });
    }
  }, [customerDetail, editBookingId]);

  useEffect(() => {
    if (!selectedBooking || isLoadingBookingRef.current) return;

    if (selectedBooking.vehicle_id === null && selectedBooking.temp_vehicle) {
      setTempVehicle(selectedBooking.temp_vehicle || "");
      setSelectedVehicle(null);
      setOtherFlags((prev) => ({ ...prev, isVehicleOther: true }));
    } else {
      setSelectedVehicle(
        allVehicles.find((v) => v.vehicle_id === selectedBooking.vehicle_id) ||
          null,
      );
      setTempVehicle("");
      setOtherFlags((prev) => ({ ...prev, isVehicleOther: false }));
    }

    if (
      selectedBooking.driver_id === null &&
      (selectedBooking.temp_driver || selectedBooking.temp_driver_number)
    ) {
      setTempDriver(
        [selectedBooking.temp_driver, selectedBooking.temp_driver_number]
          .filter(Boolean)
          .join(" "),
      );
      setSelectedDriver(null);
      setOtherFlags((prev) => ({ ...prev, isDriverOther: true }));
    } else {
      setSelectedDriver(
        allDrivers.find((d) => d.id === selectedBooking.driver_id) || null,
      );
      setTempDriver("");
      setOtherFlags((prev) => ({ ...prev, isDriverOther: false }));
    }

    let stageValue = "Customer";
    if (selectedBooking.customer_full_data?.stage)
      stageValue = selectedBooking.customer_full_data.stage;
    else if (selectedBooking.customer_stage)
      stageValue = selectedBooking.customer_stage;
    else
      stageValue =
        selectedBooking.booking_type === "B" ? "Customer" : "Cold Leads";

    setForm({
      partyCode: selectedBooking.party_code || "",
      bookingType: selectedBooking.booking_type || "B",
      customerName: selectedBooking.customer_name || "",
      customerMobile: selectedBooking.customer_mobile || "",
      tripDetails: selectedBooking.trip_details || "",
      pickupDate: selectedBooking.pickup_date || "",
      pickupTime: selectedBooking.pickup_time || "",
      pax: selectedBooking.pax?.toString() || "",
      pickupLocation:
        selectedBooking.pickupLocation || selectedBooking.pickup_location || "",
      pickupLocationLabel: selectedBooking.pickupLocationLabel || "",
      pickupAddress: selectedBooking.pickup_address || "",
      dropLocation: selectedBooking.drop_location || "",
      dropAddress: selectedBooking.drop_address || "",
      dropDate: selectedBooking.drop_date || "",
      dropTime: selectedBooking.drop_time || "",
      tripMode: selectedBooking.trip_mode || "DROP",
      quote: selectedBooking.quote || "",
      advance: selectedBooking.advance || "",
      vehicleId: selectedBooking.vehicle_id || null,
      driverId: selectedBooking.driver_id || null,
      confirmation: selectedBooking.confirmation || "WHATSAPP",
      paymentDetails: selectedBooking.payment_details || "",
      instructions: selectedBooking.instructions || "",
      vehicleType: selectedBooking.vehicle_type || "",
      stage: stageValue,
      itinerary_docs: extractPdf(
        selectedBooking.itinerary_doc || selectedBooking.itinerary_docs,
      ),
    });
    setCustomerExists(true);
    setIsEdited(false);
    manualClearRef.current = { vehicle: false, driver: false };
  }, [selectedBooking, allVehicles, allDrivers]);

  const handleChange = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    setIsEdited(true);
    if (errors[key]) setErrors((prev) => ({ ...prev, [key]: "" }));
  };

  const handlePaxChange = (e) => {
    const value = e.target.value;
    if (value === "" || /^\d+$/.test(value)) handleChange("pax", value);
  };

  const handleMobileChange = (e) => {
    let value = e.target.value.replace(/\D/g, "");
    if (value.length <= 15) {
      handleChange("customerMobile", value);
      if (value.length < 6) {
        setCustomerExists(false);
        setCustomerData(null);
      }
    }
  };

  const handleFetchByMobile = async (mobile) => {
    if (!mobile || mobile.length < 6) return;
    try {
      const res = await axios.get(
        `${API_BASE_URL}/api/travel/customers/store/${store_id}/phone/${mobile}`,
      );
      if (!res.data.success) {
        setSnackbar({
          open: true,
          message: "Customer not found",
          severity: "error",
        });
        return;
      }
      const fetched = res.data.data;
      const locations = fetched.pickupLocations || [];

      setForm((prev) => ({
        ...prev,
        customerName: fetched.name || "",
        customerMobile: fetched.mobile?.toString() || mobile,
        partyCode: fetched.party_code || "",
        pickupAddress: fetched.pickupAddress || fetched.pickup_address || "",
        stage: fetched.stage || prev.stage,
        pickupLocation: locations.length === 1 ? locations[0].link || "" : "",
        pickupLocationLabel:
          locations.length === 1 ? locations[0].label || "" : "",
      }));
      setCustomerExists(true);
      setCustomerData(fetched);
      setSelectedCustomerForPanel(fetched);
      setSelectedCustomerTrips(fetched.trips || []);
      setSnackbar({
        open: true,
        message: `Customer "${fetched.name}" loaded`,
        severity: "success",
      });

      if (locations.length > 1) {
        setAvailablePickupLocations(locations);
        setShowPickupLocationDialog(true);
      }
    } catch (err) {
      setSnackbar({
        open: true,
        message: "Failed to fetch customer",
        severity: "error",
      });
    }
  };

  const handleFetchByPartyCode = async (code) => {
    if (!code || !code.trim()) return;
    try {
      const res = await axios.get(
        `${API_BASE_URL}/api/travel/customers/store/${store_id}/party/${code.trim()}`,
      );
      if (!res.data.success) {
        setSnackbar({
          open: true,
          message: "Customer not found with this Party Code",
          severity: "error",
        });
        return;
      }
      const fetched = res.data.data;
      setForm((prev) => ({
        ...prev,
        customerName: fetched.name || "",
        customerMobile: fetched.mobile?.toString() || "",
        partyCode: fetched.party_code || code,
        pickupAddress: fetched.pickupAddress || "",
        stage: fetched.stage || prev.stage,
      }));
      setCustomerExists(true);
      setCustomerData(fetched);
      setSelectedCustomerForPanel(fetched);
      setSelectedCustomerTrips(fetched.trips || []);
      setSnackbar({
        open: true,
        message: `Customer "${fetched.name}" loaded`,
        severity: "success",
      });
    } catch (err) {
      setSnackbar({
        open: true,
        message: "Failed to fetch customer by Party Code",
        severity: "error",
      });
    }
  };

  const handleSkipPickupLocation = () => {
    setShowPickupLocationDialog(false);
  };

  const checkVehicleConflict = () => {
    if (!form.vehicleId || form.vehicleId === "other") return null;
    const pickupDateTime = new Date(
      `${form.pickupDate}T${form.pickupTime || "00:00"}`,
    );
    const dropDateTime = new Date(
      `${form.dropDate}T${form.dropTime || "23:59"}`,
    );
    const conflictingBookings = bookings.filter((booking) => {
      if (selectedBooking && booking.booking_id === selectedBooking.booking_id)
        return false;
      if (booking.vehicle_id !== form.vehicleId) return false;
      const bookingPickup = new Date(
        `${booking.pickup_date}T${booking.pickup_time || "00:00"}`,
      );
      const bookingDrop = new Date(
        `${booking.drop_date}T${booking.drop_time || "23:59"}`,
      );
      return pickupDateTime < bookingDrop && dropDateTime > bookingPickup;
    });
    return conflictingBookings.length > 0 ? conflictingBookings[0] : null;
  };

  const validateForm = () => {
    const newErrors = {};
    const mobileValue = (form.customerMobile || "").toString().trim();
    if (!mobileValue) newErrors.customerMobile = "Customer mobile is required";
    else if (mobileValue.length < 6 || mobileValue.length > 15)
      newErrors.customerMobile = "Mobile number must be 6-15 digits";
    if (!(form.customerName || "").trim())
      newErrors.customerName = "Customer name is required";
    if (!(form.tripDetails || "").trim())
      newErrors.tripDetails = "Trip details are required";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSaveBooking = async (resetAfter = true) => {
    if (isSaving) return;

    // A new booking records the branch that took it, so one has to be settled
    // first. An edit is not blocked — updateBooking leaves branch_id alone, so
    // there is nothing to get wrong.
    if (branchBlocked && !selectedBooking && !editBookingId) {
      openBranchPicker();
      return;
    }

    if (!validateForm()) {
      setSnackbar({
        open: true,
        message: "Please fill all required fields",
        severity: "error",
      });
      return;
    }
    const conflict = checkVehicleConflict();
    if (conflict) {
      const conflictPickup = new Date(
        `${conflict.pickup_date}${
          conflict.pickup_time ? "T" + conflict.pickup_time : ""
        }`,
      );
      const conflictDrop = new Date(
        `${conflict.drop_date}${
          conflict.drop_time ? "T" + conflict.drop_time : ""
        }`,
      );
      setSnackbar({
        open: true,
        message: `Vehicle already booked for ${
          conflict.booking_id
        } from ${conflictPickup.toLocaleString()} to ${conflictDrop.toLocaleString()}`,
        severity: "error",
      });
      return;
    }

    setIsSaving(true);

    const customer_id =
      customerData?.customer_id ||
      customerData?.id ||
      customerDetail?.id ||
      null;
    const resolvedVehicleId = otherFlags.isVehicleOther
      ? tempVehicle
      : form.vehicleId;
    const resolvedDriverId = otherFlags.isDriverOther
      ? tempDriver
      : form.driverId;

    const payload = {
      store_id,
      // The branch that TOOK the booking. Attribution only — the booking stays
      // visible and payable from every branch of the store, which is the point:
      // a trip booked at one counter is routinely paid for at another.
      //
      // One payload serves both the create and the update call. Only the create
      // reads it: updateBooking does not touch branch_id at all, so an edit
      // leaves the taking branch exactly as it was, which is what it should do
      // — correcting a phone number is not a reason to reattribute the booking
      // to whoever happened to open it.
      ...(branchScope?.mode === "branch" && branchScope?.branchId != null
        ? { branch_id: branchScope.branchId }
        : {}),
      created_by: user_id,
      customer_id,
      booking_type: form.bookingType,
      customer_name: (form.customerName || "").trim(),
      customer_mobile: (form.customerMobile || "").toString().trim(),
      trip_details: (form.tripDetails || "").trim(),
      pickup_date: form.pickupDate,
      pickup_time: form.pickupTime || null,
      pax: form.pax,
      pickup_location: form.pickupLocation,
      pickupLocationLabel: form.pickupLocationLabel,
      pickup_address: (form.pickupAddress || "").trim(),
      drop_location: form.dropLocation,
      drop_address: (form.dropAddress || "").trim(),
      drop_date: form.dropDate,
      drop_time: form.dropTime || null,
      trip_mode: form.tripMode,
      quote: parseFloat(form.quote) || 0,
      advance: parseFloat(form.advance) || 0,
      vehicle_id: resolvedVehicleId,
      driver_id: resolvedDriverId,
      confirmation: form.confirmation,
      created_at: new Date().toISOString(),
      payment_details: (form.paymentDetails || "").trim(),
      instructions: (form.instructions || "").trim(),
      vehicle_type: (form.vehicleType || "").trim(),
      isVehicleOther: otherFlags.isVehicleOther,
      isDriverOther: otherFlags.isDriverOther,
      stage: form.stage,
      itinerary_doc: form.itinerary_docs || null,
      is_itinerary: form.itinerary_docs ? 1 : 0,
      created_by: userInfo?.id || user_id || null,
    };

    try {
      let response;
      if (selectedBooking || editBookingId) {
        const bookingIdToUpdate = selectedBooking?.booking_id || editBookingId;
        response = await axios.put(
          `${API_BASE_URL}/api/travel/booking/${bookingIdToUpdate}`,
          payload,
        );
        if (response?.data?.data?.old_booking_id)
          setSnackbar({
            open: true,
            message: `Booking type changed! New ID: ${response.data.data.booking_id}`,
            severity: "success",
          });
      } else {
        response = await axios.post(
          `${API_BASE_URL}/api/travel/booking`,
          payload,
        );
      }
      if (response?.data?.success) {
        setSnackbar({
          open: true,
          message: "Booking saved successfully!",
          severity: "success",
        });
        navigate(location.pathname, { replace: true, state: {} });
        await fetchBookings();
        if (onAfterSave) onAfterSave();
        if (resetAfter) {
          handleReset();
        }
      } else {
        setSnackbar({
          open: true,
          message:
            response?.data?.message ||
            "Something went wrong, please try again!",
          severity: "error",
        });
      }
    } catch (error) {
      setSnackbar({
        open: true,
        message:
          error?.response?.data?.message ||
          "Server error. Please try again later.",
        severity: "error",
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeleteClick = (e, booking) => {
    e?.stopPropagation?.();
    setBookingToDelete(booking);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!bookingToDelete) {
      setDeleteDialogOpen(false);
      setBookingToDelete(null);
      setSelectedCustomerForPanel(null);
      handleReset();
      return;
    }
    setDeleting(true);
    try {
      const response = await axios.delete(
        `${API_BASE_URL}/api/travel/booking/${bookingToDelete.booking_id}`,
      );
      if (response.data.success) {
        setDeleteDialogOpen(false);
        setBookingToDelete(null);
        await fetchBookings();
        handleReset();
        setSnackbar({
          open: true,
          message: "Booking deleted successfully!",
          severity: "success",
        });
      } else {
        setSnackbar({
          open: true,
          message: response.data.statusMessage || "Failed to delete booking",
          severity: "error",
        });
      }
    } catch (err) {
      setSnackbar({
        open: true,
        message: "Network error. Failed to delete booking.",
        severity: "error",
      });
      console.error("Delete error:", err);
    } finally {
      setDeleting(false);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setBookingToDelete(null);
  };

  const handleCreateCustomer = () => {
    setShowCustomerDialog(false);
    navigate("/travel/customer-master", {
      state: { ...userDataObject, isCreateBooking: true },
    });
  };

  const isExistingBooking = !!(selectedBooking || editBookingId);

  const muiTheme = useTheme();
  const isMobile = useMediaQuery(muiTheme.breakpoints.down("sm"));

  return (
    <Paper
      sx={{
        p: { xs: 1.5, sm: 2 },
        // Below lg the form is the only visible pane — let the page scroll
        // naturally instead of trapping a long form in a nested scroller.
        height: { xs: "auto", lg: "calc(100vh - 120px)" },
        overflow: { xs: "visible", lg: "auto" },
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <ToggleButtonGroup
            value={form.bookingType}
            exclusive
            onChange={(e, v) => v && handleChange("bookingType", v)}
            fullWidth
            size="small"
            sx={{
              "& .MuiToggleButton-root.Mui-selected": {
                backgroundColor: "#3f73e2",
                color: "#fff",
                "&:hover": { backgroundColor: "#9facf7" },
              },
            }}
          >
            <ToggleButton value="B">Booking</ToggleButton>
            <ToggleButton value="E">Enquiry</ToggleButton>
          </ToggleButtonGroup>
        </Box>

        {attachedPdf && (
          <Tooltip title="Send booking via WhatsApp" arrow>
            <IconButton
              onClick={() => setWhatsappDialogOpen(true)}
              size="small"
              sx={{
                flexShrink: 0,
                bgcolor: "#25d366",
                color: "#fff",
                width: 36,
                height: 36,
                borderRadius: 1.5,
                "&:hover": {
                  bgcolor: "#1ebe5d",
                  transform: "scale(1.05)",
                },
                transition: "all 0.2s",
                boxShadow: "0 2px 8px rgba(37,211,102,0.4)",
              }}
            >
              <WhatsAppIcon sx={{ fontSize: 20 }} />
            </IconButton>
          </Tooltip>
        )}
      </Box>

      {selectedBooking && (
        <Typography
          variant="caption"
          sx={{ mb: 1.5, color: "text.secondary", display: "block" }}
        >
          Selected Booking: {selectedBooking.booking_id} -{" "}
          {selectedBooking.customer_name}
        </Typography>
      )}
      {customerDetail && !selectedBooking && (
        <Typography
          variant="caption"
          sx={{ mb: 1.5, color: "success.main", display: "block" }}
        >
          Creating booking for: {customerDetail.name || "Unknown"} (
          {customerDetail.mobile || "N/A"})
        </Typography>
      )}

      <Grid container spacing={1} sx={{ mb: 1.5 }}>
        <Grid item xs={4} sm={3} md={2}>
          <TextField
            fullWidth
            size="small"
            label="Party Code"
            value={form.partyCode}
            onChange={(e) => handleChange("partyCode", e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Tab") {
                e.preventDefault();
                handleFetchByPartyCode(form.partyCode);
              }
            }}
            onBlur={() => handleFetchByPartyCode(form.partyCode)}
            placeholder="Tab or Enter to search"
          />
        </Grid>
        <Grid item xs={8} sm={5} md={6}>
          <TextField
            fullWidth
            size="small"
            label="Customer Name *"
            value={form.customerName}
            onChange={(e) => handleChange("customerName", e.target.value)}
            error={!!errors.customerName}
            helperText={errors.customerName}
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <TextField
            fullWidth
            size="small"
            label="Customer Mobile *"
            value={form.customerMobile}
            onChange={handleMobileChange}
            error={!!errors.customerMobile}
            helperText={errors.customerMobile}
            inputProps={{ maxLength: 15 }}
            placeholder="6-15 digits"
            ref={mobileInputRef}
            onKeyDown={(e) => {
              if (e.key === "Tab") {
                mobileTabPressedRef.current = true;
                handleFetchByMobile(form.customerMobile);
              }
            }}
            onBlur={() => {
              if (mobileTabPressedRef.current) {
                mobileTabPressedRef.current = false;
                return;
              }
              handleFetchByMobile(form.customerMobile);
            }}
          />
        </Grid>
      </Grid>

      <TextField
        fullWidth
        size="small"
        label="Trip Details *"
        multiline
        rows={2}
        value={form.tripDetails}
        onChange={(e) => handleChange("tripDetails", e.target.value)}
        error={!!errors.tripDetails}
        helperText={errors.tripDetails}
        sx={{ mb: 1.5 }}
      />

      {attachedPdf && (
        <Box sx={{ mb: 1.5, display: "flex", alignItems: "center", gap: 1 }}>
          <Tooltip title="Click to open PDF in new tab">
            <Chip
              icon={<PictureAsPdfIcon />}
              label={
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                  <span
                    style={{
                      maxWidth: 180,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                      display: "inline-block",
                    }}
                  >
                    {attachedPdf.fileName}
                  </span>
                  <LaunchIcon sx={{ fontSize: 13, opacity: 0.7 }} />
                </Box>
              }
              onClick={handleOpenPdfInNewTab}
              onDelete={handleRemoveItineraryPdf}
              color="primary"
              variant="outlined"
              sx={{
                cursor: "pointer",
                maxWidth: "100%",
                "& .MuiChip-label": { display: "flex", alignItems: "center" },
                "&:hover": { bgcolor: "#e3f2fd" },
              }}
            />
          </Tooltip>
          <Typography variant="caption" color="text.secondary">
            Itinerary PDF attached
          </Typography>
        </Box>
      )}

      {/* ========== REPLACED DateRangePicker with two SingleDatePickers ========== */}
      <Box sx={{ mb: 1.5 }}>
        <Typography
          variant="caption"
          sx={{
            display: "block",
            mb: 0.5,
            fontWeight: 500,
            color: "text.secondary",
          }}
        >
          Pickup & Drop Date / Time
        </Typography>
        <Grid container spacing={1.5}>
          <Grid item xs={12} sm={6}>
            <SingleDatePicker
              label="Pickup Date & Time"
              date={form.pickupDate}
              time={form.pickupTime}
              onDateChange={(val) => handleChange("pickupDate", val)}
              onTimeChange={(val) => handleChange("pickupTime", val)}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <SingleDatePicker
              label="Drop Date & Time"
              date={form.dropDate}
              time={form.dropTime}
              onDateChange={(val) => handleChange("dropDate", val)}
              onTimeChange={(val) => handleChange("dropTime", val)}
            />
          </Grid>
        </Grid>
        {errors.dropDate && (
          <Typography
            variant="caption"
            color="error"
            sx={{ mt: 0.5, display: "block" }}
          >
            {errors.dropDate}
          </Typography>
        )}
      </Box>

      <Grid container spacing={1.5} sx={{ mb: 1.5 }} alignItems="center">
        <Grid item xs={8} sm={4}>
          <TextField
            fullWidth
            size="small"
            label="Vehicle Type"
            value={form.vehicleType}
            onChange={(e) =>
              setForm((prev) => ({ ...prev, vehicleType: e.target.value }))
            }
            InputLabelProps={{ shrink: true }}
          />
        </Grid>
        <Grid item xs={4} sm={2}>
          <TextField
            fullWidth
            size="small"
            label="Pax"
            value={form.pax}
            onChange={handlePaxChange}
            error={!!errors.pax}
            helperText={errors.pax}
            inputProps={{ inputMode: "numeric", pattern: "[0-9]*" }}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <FormControl fullWidth size="small">
            <InputLabel>Stage</InputLabel>
            <Select
              value={form.stage}
              label="Stage"
              onChange={(e) => handleChange("stage", e.target.value)}
            >
              {STAGE_OPTIONS.map((stage) => (
                <MenuItem key={stage} value={stage}>
                  {stage}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
      </Grid>

      <Grid container spacing={1.5} sx={{ mb: 1.5 }}>
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            size="small"
            label="Pickup Address"
            multiline
            rows={2}
            value={form.pickupAddress}
            onChange={(e) => handleChange("pickupAddress", e.target.value)}
            error={!!errors.pickupAddress}
            helperText={errors.pickupAddress}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            size="small"
            label="Pickup Location"
            multiline
            rows={2}
            value={form.pickupLocation}
            onChange={(e) => handleChange("pickupLocation", e.target.value)}
            error={!!errors.pickupLocation}
            helperText={errors.pickupLocation}
          />
          {form.pickupLocationLabel && (
            <Box
              onClick={() => setShowPickupLocationDialog(true)}
              sx={{
                mt: 0.5,
                display: "flex",
                alignItems: "center",
                gap: 0.5,
                cursor: "pointer",
              }}
            >
              <LocationOnIcon sx={{ fontSize: 14, color: "primary.main" }} />
              <Typography variant="caption" color="primary">
                {form.pickupLocationLabel}
              </Typography>
            </Box>
          )}
        </Grid>
      </Grid>

      <Grid item xs={12}>
        <TextField
          sx={{ mb: 1 }}
          fullWidth
          multiline
          rows={2}
          size="small"
          label="Payment Details"
          value={form.paymentDetails}
          onChange={(e) => handleChange("paymentDetails", e.target.value)}
        />
      </Grid>

      <Button
        fullWidth
        size="small"
        variant="outlined"
        onClick={() => setShowMoreFields(!showMoreFields)}
        endIcon={showMoreFields ? <ExpandLessIcon /> : <ExpandMoreIcon />}
        sx={{ mb: 1.5 }}
      >
        {showMoreFields ? "Show Less" : "Show More Fields"}
      </Button>

      <Collapse in={showMoreFields}>
        <TextField
          fullWidth
          multiline
          rows={2}
          size="small"
          label="Special Instructions"
          value={form.instructions}
          onChange={(e) => handleChange("instructions", e.target.value)}
          sx={{ mb: 1.5 }}
        />
        <Grid container spacing={1.5} sx={{ mb: 1.5 }}>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              size="small"
              label="Drop Address"
              multiline
              rows={3.3}
              value={form.dropAddress}
              onChange={(e) => handleChange("dropAddress", e.target.value)}
              error={!!errors.dropAddress}
              helperText={errors.dropAddress}
              sx={{ mb: 1.5 }}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              size="small"
              label="Drop Location"
              value={form.dropLocation}
              onChange={(e) => handleChange("dropLocation", e.target.value)}
              error={!!errors.dropLocation}
              helperText={errors.dropLocation}
            />
          </Grid>
        </Grid>

        <Grid container spacing={1.5} sx={{ mb: 1.5 }}>
          <Grid item xs={12} sm={6}>
            <ToggleButtonGroup
              value={form.tripMode}
              exclusive
              onChange={(e, v) => v && handleChange("tripMode", v)}
              fullWidth
              size="small"
              sx={{ mb: 1.5 }}
            >
              <ToggleButton value="DROP">Drop</ToggleButton>
              <ToggleButton value="PICKUP">Pickup</ToggleButton>
              <ToggleButton value="ROUND">ROUND</ToggleButton>
            </ToggleButtonGroup>
          </Grid>
          <Grid item xs={6} sm={3}>
            {tempVehicle ? (
              <TextField
                size="small"
                fullWidth
                label="Vehicle (Other)"
                value={tempVehicle}
                onChange={(e) => setTempVehicle(e.target.value)}
                InputProps={{
                  endAdornment: (
                    <IconButton
                      size="small"
                      onClick={() => {
                        setTempVehicle("");
                        setOtherFlags((prev) => ({
                          ...prev,
                          isVehicleOther: false,
                        }));
                        handleChange("vehicleId", null);
                        manualClearRef.current.vehicle = true;
                      }}
                    >
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  ),
                }}
              />
            ) : selectedVehicle?.vehicle_id === "other" ? (
              <TextField
                size="small"
                fullWidth
                label="Vehicle (Other)"
                placeholder="Enter vehicle name/number"
                value={tempVehicle}
                onChange={(e) => setTempVehicle(e.target.value)}
                InputProps={{
                  endAdornment: (
                    <IconButton
                      size="small"
                      onClick={() => {
                        setSelectedVehicle(null);
                        setTempVehicle("");
                        setOtherFlags((prev) => ({
                          ...prev,
                          isVehicleOther: false,
                        }));
                        handleChange("vehicleId", null);
                        manualClearRef.current.vehicle = true;
                      }}
                    >
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  ),
                }}
              />
            ) : (
              <Autocomplete
                size="small"
                options={availableVehicles}
                value={selectedVehicle}
                onChange={(e, newValue) => {
                  if (newValue) {
                    manualClearRef.current.vehicle = false;
                    manualClearRef.current.driver = false;
                  } else {
                    manualClearRef.current.vehicle = true;
                  }
                  setSelectedVehicle(newValue);
                  if (newValue?.vehicle_id === "other") {
                    handleChange("vehicleId", "");
                    setOtherFlags((prev) => ({
                      ...prev,
                      isVehicleOther: true,
                    }));
                  } else {
                    handleChange("vehicleId", newValue?.vehicle_id || null);
                    setOtherFlags((prev) => ({
                      ...prev,
                      isVehicleOther: false,
                    }));
                  }
                  if (newValue && tempVehicle) setTempVehicle("");
                }}
                getOptionLabel={(option) =>
                  option.vehicle_id === "other"
                    ? "Other (Enter Manually)"
                    : `${option.vehicle_code || "Unknown"}`
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Vehicle"
                    placeholder="(Assign Later)"
                  />
                )}
                isOptionEqualToValue={(option, value) =>
                  option.vehicle_id === value?.vehicle_id
                }
              />
            )}
          </Grid>
          <Grid item xs={6} sm={3}>
            {tempDriver ? (
              <TextField
                size="small"
                fullWidth
                label="Driver (Other)"
                value={tempDriver}
                onChange={(e) => setTempDriver(e.target.value)}
                InputProps={{
                  endAdornment: (
                    <IconButton
                      size="small"
                      onClick={() => {
                        setTempDriver("");
                        setOtherFlags((prev) => ({
                          ...prev,
                          isDriverOther: false,
                        }));
                        handleChange("driverId", null);
                        manualClearRef.current.driver = true;
                      }}
                    >
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  ),
                }}
              />
            ) : selectedDriver?.id === "other" ? (
              <TextField
                size="small"
                fullWidth
                label="Driver (Other)"
                placeholder="Driver name + Mobile"
                value={tempDriver}
                onChange={(e) => setTempDriver(e.target.value)}
                InputProps={{
                  endAdornment: (
                    <IconButton
                      size="small"
                      onClick={() => {
                        setSelectedDriver(null);
                        setTempDriver("");
                        setOtherFlags((prev) => ({
                          ...prev,
                          isDriverOther: false,
                        }));
                        handleChange("driverId", null);
                        manualClearRef.current.driver = true;
                      }}
                    >
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  ),
                }}
              />
            ) : (
              <Autocomplete
                size="small"
                options={availableDrivers}
                value={selectedDriver}
                onChange={(e, newValue) => {
                  if (newValue) {
                    manualClearRef.current.driver = false;
                    manualClearRef.current.vehicle = false;
                  } else {
                    manualClearRef.current.driver = true;
                  }
                  setSelectedDriver(newValue);
                  if (newValue?.id === "other") {
                    handleChange("driverId", "");
                    setOtherFlags((prev) => ({ ...prev, isDriverOther: true }));
                  } else {
                    handleChange("driverId", newValue?.id || null);
                    setOtherFlags((prev) => ({
                      ...prev,
                      isDriverOther: false,
                    }));
                  }
                  if (newValue && tempDriver) setTempDriver("");
                }}
                getOptionLabel={(option) =>
                  option.id === "other"
                    ? "Other (Enter Manually)"
                    : `${option.driver_code || "Unknown"}`
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Driver"
                    placeholder="(Assign Later)"
                  />
                )}
                isOptionEqualToValue={(option, value) =>
                  option.id === value?.id
                }
              />
            )}
          </Grid>
        </Grid>

        <Typography
          variant="caption"
          sx={{ mb: 0.5, fontWeight: 500, display: "block" }}
        >
          Confirmation Type
        </Typography>
        <ToggleButtonGroup
          value={form.confirmation}
          exclusive
          onChange={(e, v) => v && handleChange("confirmation", v)}
          fullWidth
          size="small"
          sx={{ mb: 2 }}
        >
          <ToggleButton value="WHATSAPP">WhatsApp</ToggleButton>
          <ToggleButton value="CALL">Call</ToggleButton>
          <ToggleButton value="SMS">SMS</ToggleButton>
        </ToggleButtonGroup>
      </Collapse>

      <Grid container spacing={1.5}>
        <Grid item xs={isExistingBooking ? 6 : 6}>
          <Button
            fullWidth
            size="small"
            variant="contained"
            onClick={() => handleSaveBooking(true)}
            disabled={isSaving}
            startIcon={
              isSaving ? <CircularProgress size={16} color="inherit" /> : null
            }
          >
            {isSaving
              ? "Saving..."
              : selectedBooking
              ? "Save Changes"
              : form.bookingType === "B"
              ? "Save Booking"
              : "Save Enquiry"}
          </Button>
        </Grid>

        {!isExistingBooking && (
          <Grid item xs={6}>
            <Button
              fullWidth
              size="small"
              variant="outlined"
              color="primary"
              onClick={() => handleSaveBooking(false)}
              disabled={isSaving}
            >
              Add
            </Button>
          </Grid>
        )}

        {isExistingBooking && (
          <Grid item xs={6}>
            <Button
              fullWidth
              size="small"
              variant="outlined"
              color="error"
              onClick={(e) => handleDeleteClick(e, selectedBooking)}
            >
              Cancel
            </Button>
          </Grid>
        )}
      </Grid>

      <Dialog
        open={showPickupLocationDialog}
        onClose={handleSkipPickupLocation}
        fullWidth
        maxWidth="sm"
        fullScreen={isMobile}
      >
        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          Select Pickup Location
          <IconButton onClick={handleSkipPickupLocation}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent dividers sx={{ p: 0 }}>
          <List>
            {availablePickupLocations.map((location, index) => (
              <Box key={index}>
                <ListItemButton
                  onClick={() => {
                    setForm((prev) => ({
                      ...prev,
                      pickupLocation: location.link || "",
                      pickupLocationLabel: location.label || "",
                    }));

                    setShowPickupLocationDialog(false);

                    setSnackbar({
                      open: true,
                      message: `Pickup location "${location.label}" selected`,
                      severity: "success",
                    });
                  }}
                >
                  <ListItemIcon>
                    <LocationOnIcon color="primary" />
                  </ListItemIcon>

                  <ListItemText
                    primary={location.label}
                    secondary={location.link}
                  />
                </ListItemButton>

                {index !== availablePickupLocations.length - 1 && <Divider />}
              </Box>
            ))}
          </List>
        </DialogContent>
      </Dialog>

      <Dialog
        open={showCustomerDialog}
        onClose={() => setShowCustomerDialog(false)}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>Customer Not Found</DialogTitle>
        <DialogContent>
          <DialogContentText>
            The customer with mobile number {form.customerMobile} does not exist
            in the system. Would you like to create a new customer profile?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowCustomerDialog(false)} color="inherit">
            Continue anyway
          </Button>
          <Button onClick={handleCreateCustomer} variant="contained" autoFocus>
            Create Customer
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={deleteDialogOpen}
        onClose={handleDeleteCancel}
        aria-labelledby="delete-dialog-title"
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle id="delete-dialog-title">Confirm Delete</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete this booking?
            {bookingToDelete && (
              <Box sx={{ mt: 2, p: 1.5, bgcolor: "grey.100", borderRadius: 1 }}>
                <Typography variant="body2" fontWeight={600}>
                  Booking ID: {bookingToDelete.booking_id}
                </Typography>
                <Typography variant="body2">
                  Customer: {bookingToDelete.customer_name}
                </Typography>
              </Box>
            )}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel} disabled={deleting}>
            Cancel
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            color="error"
            variant="contained"
            disabled={deleting}
            startIcon={
              deleting ? <CircularProgress size={16} /> : <DeleteIcon />
            }
          >
            {deleting ? "Deleting..." : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>

      <WhatsAppBookingDialog
        open={whatsappDialogOpen}
        onClose={() => setWhatsappDialogOpen(false)}
        form={form}
        setSnackbar={setSnackbar}
      />

      {/* Opens only if Save was pressed on a new booking while every branch was
          on screen. Renders nothing otherwise. */}
      {branchGuardDialog}
    </Paper>
  );
}
